import React, { useMemo, useState } from "react";
import "chart.js/auto";

import ChartCard from "../common/charts/ChartCard";
import BarChart from "../common/charts/BarChart";
import AreaChart from "../common/charts/AreaChart";
import LineChart from "../common/charts/LineChart";
import DoughnutChart from "../common/charts/DoughnutChart";
import PieChart from "../common/charts/PieChart";
import PolarAreaChart from "../common/charts/PolarAreaChart";
import TooltipText from "../common/tooltipText";

import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import MilitaryTechOutlinedIcon from "@mui/icons-material/MilitaryTechOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";

const OverallDashboardView = ({ stats, courses, toMinutes }) => {
  const safeStats = stats || {};
  const safeCourses = Array.isArray(courses) ? courses : [];

  const getLeadingCourseMeta = (idx) => {
    if (idx === 0) return { cls: "rank-1", Icon: EmojiEventsOutlinedIcon };
    if (idx === 1) return { cls: "rank-2", Icon: WorkspacePremiumOutlinedIcon };
    if (idx === 2) return { cls: "rank-3", Icon: MilitaryTechOutlinedIcon };
    return { cls: "rank-other", Icon: TrendingUpOutlinedIcon };
  };

  const chartPalette = useMemo(
    () => ({
      solid: {
        orange: "rgba(252, 164, 59, 0.85)",
        mint: "rgba(133, 238, 173, 0.85)",
        lime: "rgba(212, 249, 45, 0.85)",
        yellow: "rgba(250, 238, 112, 0.85)",
        slate: "rgba(148, 163, 184, 0.65)",
      },
      hover: {
        orange: "rgba(252, 164, 59, 0.95)",
        mint: "rgba(133, 238, 173, 0.95)",
        lime: "rgba(212, 249, 45, 0.95)",
        yellow: "rgba(250, 238, 112, 0.95)",
        slate: "rgba(148, 163, 184, 0.85)",
      },
      soft: {
        orange: "rgba(252, 164, 59, 0.16)",
        mint: "rgba(133, 238, 173, 0.16)",
        lime: "rgba(212, 249, 45, 0.16)",
        yellow: "rgba(250, 238, 112, 0.16)",
        slate: "rgba(148, 163, 184, 0.16)",
      },
      accents: [
        "rgba(252, 164, 59, 0.85)",
        "rgba(133, 238, 173, 0.85)",
        "rgba(212, 249, 45, 0.85)",
        "rgba(250, 238, 112, 0.85)",
        "rgba(148, 163, 184, 0.65)",
      ],
      accentsHover: [
        "rgba(252, 164, 59, 0.95)",
        "rgba(133, 238, 173, 0.95)",
        "rgba(212, 249, 45, 0.95)",
        "rgba(250, 238, 112, 0.95)",
        "rgba(148, 163, 184, 0.85)",
      ],
    }),
    [],
  );

  const sliceColors = (count) =>
    Array.from(
      { length: count },
      (_, i) => chartPalette.accents[i % chartPalette.accents.length],
    );

  const sliceHoverColors = (count) =>
    Array.from(
      { length: count },
      (_, i) => chartPalette.accentsHover[i % chartPalette.accentsHover.length],
    );

  const [hoveredLegend, setHoveredLegend] = useState(null);

  const commonChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "left",
        align: "center",
        labels: {
          boxWidth: 12,
          padding: 15,
          font: { size: 11 },
          generateLabels: (chart) => {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              return data.labels.map((label, i) => {
                const meta = chart.getDatasetMeta(0);
                const style = meta.controller.getStyle(i);
                const maxLength = 15;
                const truncatedLabel =
                  label.length > maxLength
                    ? label.substring(0, maxLength) + "..."
                    : label;
                return {
                  text: truncatedLabel,
                  fullText: label,
                  fillStyle: style.backgroundColor,
                  strokeStyle: style.borderColor,
                  lineWidth: style.borderWidth,
                  hidden: !chart.getDataVisibility(i),
                  index: i,
                };
              });
            }
            return [];
          },
        },
        onHover: (event, legendItem) => {
          if (
            legendItem &&
            legendItem.fullText &&
            legendItem.fullText.length > 15
          ) {
            setHoveredLegend({
              text: legendItem.fullText,
              x: event.native.clientX,
              y: event.native.clientY,
            });
          } else {
            setHoveredLegend(null);
          }
        },
        onLeave: () => {
          setHoveredLegend(null);
        },
      },
      tooltip: {
        callbacks: {
          title: (items) => items[0].label,
        },
      },
    },
  };

  const courseCompletionLeaderboard = Array.isArray(
    safeStats?.course_completion?.leaderboard,
  )
    ? safeStats.course_completion.leaderboard
    : [];

  const activeRecallLeaderboard = Array.isArray(
    safeStats?.active_recall?.leaderboard,
  )
    ? safeStats.active_recall.leaderboard
    : [];

  const timeSpentLeaderboard = Array.isArray(safeStats?.time_spent?.leaderboard)
    ? safeStats.time_spent.leaderboard
    : [];

  const testsLeaderboard = Array.isArray(safeStats?.tests?.leaderboard)
    ? safeStats.tests.leaderboard
    : [];
  const quizzesLeaderboard = Array.isArray(safeStats?.quizzes?.leaderboard)
    ? safeStats.quizzes.leaderboard
    : [];
  const adaptiveLeaderboard = Array.isArray(
    safeStats?.adaptive_learning?.leaderboard,
  )
    ? safeStats.adaptive_learning.leaderboard
    : [];

  const leadingCoursesLeaderboard = Array.isArray(
    safeStats?.leading_courses?.leaderboard,
  )
    ? safeStats.leading_courses.leaderboard
    : [];

  const coinsLeaderboard = Array.isArray(safeStats?.coins?.leaderboard)
    ? safeStats.coins.leaderboard
    : [];

  const xpLeaderboard = Array.isArray(safeStats?.xp?.leaderboard)
    ? safeStats.xp.leaderboard
    : [];

  return (
    <section className="dashboard-course-section">
      <div className="dashboard-course-grid">
        <ChartCard title="Course Completion (%) by Course">
          <BarChart
            data={{
              labels: safeCourses.map((c) => c.course_name),
              datasets: [
                {
                  label: "Student Avg (%)",
                  data: safeCourses.map(
                    (c) => Number(c.course_completion_percentage) || 0,
                  ),
                  backgroundColor: chartPalette.solid.mint,
                  hoverBackgroundColor: chartPalette.hover.mint,
                  borderRadius: 10,
                },
                {
                  label: "Batch Avg (%)",
                  data: safeCourses.map(
                    (c) =>
                      Number(c.batch_wise_avg_course_completion_percentage) ||
                      0,
                  ),
                  backgroundColor: chartPalette.solid.lime,
                  hoverBackgroundColor: chartPalette.hover.lime,
                  borderRadius: 10,
                },
                {
                  label: "Teacher Avg (%)",
                  data: safeCourses.map(
                    (c) => Number(c.teacher_course_completion_percentage) || 0,
                  ),
                  backgroundColor: chartPalette.solid.orange,
                  hoverBackgroundColor: chartPalette.hover.orange,
                  borderRadius: 10,
                },
              ],
            }}
            options={{
              responsive: true,
              plugins: { legend: { display: true, position: "bottom" } },
              scales: { y: { beginAtZero: true, max: 100 } },
            }}
          />
        </ChartCard>

        <ChartCard title="Active Recall Completed by Course">
          <BarChart
            data={{
              labels: safeCourses.map((c) => c.course_name),
              datasets: [
                {
                  label: "Active Recall Completed",
                  data: safeCourses.map(
                    (c) => Number(c.total_active_recall_completed) || 0,
                  ),
                  backgroundColor: chartPalette.solid.lime,
                  hoverBackgroundColor: chartPalette.hover.lime,
                  borderRadius: 10,
                },
              ],
            }}
            options={{ responsive: true, scales: { y: { beginAtZero: true } } }}
          />
        </ChartCard>

        <ChartCard title="Homework Completion (%) by Course">
          <BarChart
            data={{
              labels: safeCourses.map((c) => c.course_name),
              datasets: [
                {
                  label: "Teacher Avg (%)",
                  data: safeCourses.map(
                    (c) => Number(c.homework_completion_rate) || 0,
                  ),
                  backgroundColor: chartPalette.solid.orange,
                  hoverBackgroundColor: chartPalette.hover.orange,
                  borderRadius: 10,
                },
                {
                  label: "Student Avg (%)",
                  data: safeCourses.map(
                    (c) => Number(c.homework_user_completion_rate) || 0,
                  ),
                  backgroundColor: chartPalette.solid.mint,
                  hoverBackgroundColor: chartPalette.hover.mint,
                  borderRadius: 10,
                },
              ],
            }}
            options={{
              responsive: true,
              plugins: { legend: { display: true, position: "bottom" } },
              scales: {
                x: { stacked: true },
                y: { beginAtZero: true, max: 100, stacked: true },
              },
            }}
          />
        </ChartCard>

        <ChartCard title="Time Spent (minutes) by Course">
          <AreaChart
            data={{
              labels: safeCourses.map((c) => c.course_name),
              datasets: [
                {
                  label: "Time Spent (min)",
                  data: safeCourses.map(
                    (c) => toMinutes?.(c.total_time_spent_seconds) || 0,
                  ),
                  borderColor: chartPalette.solid.orange,
                  backgroundColor: chartPalette.soft.orange,
                  fill: true,
                  tension: 0.35,
                  pointRadius: 2,
                },
              ],
            }}
            options={{ responsive: true, scales: { y: { beginAtZero: true } } }}
          />
        </ChartCard>

        <ChartCard title="Quiz Completion Rate (%) by Course">
          <BarChart
            data={{
              labels: safeCourses.map((c) => c.course_name),
              datasets: [
                {
                  label: "Teacher Avg (%)",
                  data: safeCourses.map(
                    (c) => Number(c.quiz_completion_rate) || 0,
                  ),
                  backgroundColor: chartPalette.solid.orange,
                  hoverBackgroundColor: chartPalette.hover.orange,
                  borderRadius: 10,
                },
                {
                  label: "Student Avg (%)",
                  data: safeCourses.map(
                    (c) => Number(c.quiz_user_completion_rate) || 0,
                  ),
                  backgroundColor: chartPalette.solid.mint,
                  hoverBackgroundColor: chartPalette.hover.mint,
                  borderRadius: 10,
                },
              ],
            }}
            options={{
              responsive: true,
              plugins: { legend: { display: true, position: "bottom" } },
              scales: { y: { beginAtZero: true, max: 100 } },
            }}
          />
        </ChartCard>

        <ChartCard title="Tests Completion Rate (%) by Course">
          <LineChart
            data={{
              labels: safeCourses.map((c) => c.course_name),
              datasets: [
                {
                  label: "Teacher Avg (%)",
                  data: safeCourses.map(
                    (c) => Number(c.test_completion_rate) || 0,
                  ),
                  borderColor: chartPalette.solid.orange,
                  backgroundColor: chartPalette.soft.orange,
                  fill: false,
                  tension: 0.35,
                  pointRadius: 3,
                  pointBackgroundColor: chartPalette.solid.orange,
                  pointHoverBackgroundColor: chartPalette.hover.orange,
                  pointBorderColor: "rgba(15, 23, 42, 1)",
                  pointHoverBorderColor: "rgba(15, 23, 42, 1)",
                },
                {
                  label: "Student Avg (%)",
                  data: safeCourses.map(
                    (c) => Number(c.test_user_completion_rate) || 0,
                  ),
                  borderColor: chartPalette.solid.mint,
                  backgroundColor: chartPalette.soft.mint,
                  fill: false,
                  tension: 0.35,
                  pointRadius: 3,
                  pointBackgroundColor: chartPalette.solid.mint,
                  pointHoverBackgroundColor: chartPalette.hover.mint,
                  pointBorderColor: "rgba(15, 23, 42, 1)",
                  pointHoverBorderColor: "rgba(15, 23, 42, 1)",
                },
              ],
            }}
            options={{
              responsive: true,
              plugins: { legend: { display: true, position: "bottom" } },
              scales: { y: { beginAtZero: true, max: 100 } },
            }}
          />
        </ChartCard>

        <ChartCard title="Adaptive Completion Rate (%) by Course">
          <AreaChart
            data={{
              labels: safeCourses.map((c) => c.course_name),
              datasets: [
                {
                  label: "Adaptive Completion (%)",
                  data: safeCourses.map(
                    (c) => Number(c.adaptive_completion_rate) || 0,
                  ),
                  borderColor: chartPalette.solid.yellow,
                  backgroundColor: chartPalette.soft.yellow,
                  fill: true,
                  tension: 0.35,
                  pointRadius: 2,
                },
              ],
            }}
            options={{
              responsive: true,
              scales: { y: { beginAtZero: true, max: 100 } },
            }}
          />
        </ChartCard>

        <ChartCard title="Coins by Course">
          <BarChart
            data={{
              labels: safeCourses.map((c) => c.course_name),
              datasets: [
                {
                  label: "Coins",
                  data: safeCourses.map((c) => Number(c.total_coins) || 0),
                  backgroundColor: chartPalette.solid.yellow,
                  hoverBackgroundColor: chartPalette.hover.yellow,
                  borderRadius: 10,
                },
              ],
            }}
            options={{ responsive: true, scales: { y: { beginAtZero: true } } }}
          />
        </ChartCard>

        <ChartCard title="XP by Course">
          <AreaChart
            data={{
              labels: safeCourses.map((c) => c.course_name),
              datasets: [
                {
                  label: "XP",
                  data: safeCourses.map((c) => Number(c.total_xp) || 0),
                  borderColor: chartPalette.solid.lime,
                  backgroundColor: chartPalette.soft.lime,
                  fill: true,
                  tension: 0.35,
                  pointRadius: 2,
                },
              ],
            }}
            options={{ responsive: true, scales: { y: { beginAtZero: true } } }}
          />
        </ChartCard>
      </div>

      <div className="dashboard-course-grid">
        <div
          className="leaderboard-section-header"
          style={{ gridColumn: "1 / -1", marginTop: "24px" }}
        >
          <h2
            style={{
              fontSize: "20px",
              fontWeight: "700",
              color: "var(--color-text-dark)",
              marginBottom: "8px",
            }}
          >
            Leaderboards
          </h2>
        </div>

        {/* Row 1: Leading Courses and Course Completion */}
        <ChartCard
          title="Leading Courses (Avg Score)"
          className="leading-courses-leaderboard"
        >
          <div className="overview-grid">
            {leadingCoursesLeaderboard.slice(0, 5).map((c, idx) => {
              const meta = getLeadingCourseMeta(idx);
              const Icon = meta.Icon;
              return (
                <div
                  key={c?.course_id || c?.course_key || c?.course_name || idx}
                  className={`card overview-card leading-course-tile ${meta.cls}`.trim()}
                >
                  <div className="overview-card-content">
                    <div className="overview-card-text">
                      <div className="leading-course-header">
                        <div className="leading-course-badge">
                          <Icon className="leading-course-icon" />
                          <span>#{idx + 1}</span>
                        </div>
                        <div className="leading-course-score">
                          {Number(c?.overall_avg_score) || 0}
                        </div>
                      </div>
                      <strong className="leading-course-name">
                        {c?.course_name || "N/A"}
                      </strong>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </ChartCard>

        <ChartCard title="Course Completion (%)">
          <DoughnutChart
            data={{
              labels: courseCompletionLeaderboard.map((c) => c.course_name),
              datasets: [
                {
                  label: "Completion (%)",
                  data: courseCompletionLeaderboard.map(
                    (c) => Number(c.course_completion_percentage) || 0,
                  ),
                  backgroundColor: sliceColors(
                    courseCompletionLeaderboard.length,
                  ),
                  hoverBackgroundColor: sliceHoverColors(
                    courseCompletionLeaderboard.length,
                  ),
                  borderColor: "rgba(255, 255, 255, 0.9)",
                  borderWidth: 1,
                },
              ],
            }}
            options={commonChartOptions}
          />
        </ChartCard>

        {/* Row 2: Active Recall and Adaptive Learning */}
        <ChartCard title="Active Recall Completed">
          <PolarAreaChart
            data={{
              labels: activeRecallLeaderboard.map((c) => c.course_name),
              datasets: [
                {
                  label: "Active Recall Completed",
                  data: activeRecallLeaderboard.map(
                    (c) => Number(c.total_active_recall_completed) || 0,
                  ),
                  backgroundColor: sliceColors(activeRecallLeaderboard.length),
                },
              ],
            }}
            options={commonChartOptions}
          />
        </ChartCard>

        <ChartCard title="Adaptive Completion (%)">
          <AreaChart
            data={{
              labels: adaptiveLeaderboard.map((c) => c.course_name),
              datasets: [
                {
                  label: "Adaptive Completion (%)",
                  data: adaptiveLeaderboard.map(
                    (c) => Number(c.adaptive_completion_rate) || 0,
                  ),
                  borderColor: chartPalette.solid.yellow,
                  backgroundColor: chartPalette.soft.yellow,
                  fill: true,
                  tension: 0.35,
                  pointRadius: 2,
                },
              ],
            }}
            options={{
              responsive: true,
              scales: { y: { beginAtZero: true, max: 100 } },
            }}
          />
        </ChartCard>

        {/* Row 3: Coins and XP */}
        <ChartCard title="Coins">
          <PieChart
            data={{
              labels: coinsLeaderboard.map((c) => c.course_name),
              datasets: [
                {
                  label: "Coins",
                  data: coinsLeaderboard.map((c) => Number(c.total_coins) || 0),
                  backgroundColor: sliceColors(coinsLeaderboard.length),
                  hoverBackgroundColor: sliceHoverColors(
                    coinsLeaderboard.length,
                  ),
                  borderColor: "rgba(255, 255, 255, 0.9)",
                  borderWidth: 1,
                },
              ],
            }}
            options={commonChartOptions}
          />
        </ChartCard>

        <ChartCard title="XP">
          <DoughnutChart
            data={{
              labels: xpLeaderboard.map((c) => c.course_name),
              datasets: [
                {
                  label: "XP",
                  data: xpLeaderboard.map((c) => Number(c.total_xp) || 0),
                  backgroundColor: sliceColors(xpLeaderboard.length),
                  hoverBackgroundColor: sliceHoverColors(xpLeaderboard.length),
                  borderColor: "rgba(255, 255, 255, 0.9)",
                  borderWidth: 1,
                },
              ],
            }}
            options={commonChartOptions}
          />
        </ChartCard>

        {/* Row 4: Quizzes and Tests */}
        <ChartCard title="Quizzes">
          <BarChart
            data={{
              labels: quizzesLeaderboard.map((c) => c.course_name),
              datasets: [
                {
                  label: "Completed Slots",
                  data: quizzesLeaderboard.map(
                    (c) => Number(c.total_quiz_completed_slots) || 0,
                  ),
                  backgroundColor: chartPalette.solid.lime,
                  hoverBackgroundColor: chartPalette.hover.lime,
                  borderRadius: 10,
                },
                {
                  label: "Remaining Slots",
                  data: quizzesLeaderboard.map((c) => {
                    const assigned = Number(c.total_quiz_assigned) || 0;
                    const completed = Number(c.total_quiz_completed_slots) || 0;
                    return Math.max(0, assigned - completed);
                  }),
                  backgroundColor: chartPalette.soft.slate,
                  hoverBackgroundColor: chartPalette.soft.slate,
                  borderRadius: 10,
                },
              ],
            }}
            options={{
              responsive: true,
              plugins: {
                legend: { display: true, position: "bottom" },
                tooltip: {
                  callbacks: {
                    afterBody: (items) => {
                      const idx = items[0].dataIndex;
                      const rate =
                        quizzesLeaderboard[idx]?.quiz_completion_rate || 0;
                      return `Completion Rate: ${rate}%`;
                    },
                  },
                },
              },
              scales: {
                x: { stacked: true },
                y: { beginAtZero: true, stacked: true },
              },
            }}
          />
        </ChartCard>

        <ChartCard title="Tests">
          <BarChart
            data={{
              labels: testsLeaderboard.map((c) => c.course_name),
              datasets: [
                {
                  label: "Completed Slots",
                  data: testsLeaderboard.map(
                    (c) => Number(c.total_test_completed_slots) || 0,
                  ),
                  backgroundColor: chartPalette.solid.orange,
                  hoverBackgroundColor: chartPalette.hover.orange,
                  borderRadius: 10,
                },
                {
                  label: "Remaining Slots",
                  data: testsLeaderboard.map((c) => {
                    const assigned = Number(c.total_test_assigned) || 0;
                    const completed = Number(c.total_test_completed_slots) || 0;
                    return Math.max(0, assigned - completed);
                  }),
                  backgroundColor: chartPalette.soft.slate,
                  hoverBackgroundColor: chartPalette.soft.slate,
                  borderRadius: 10,
                },
              ],
            }}
            options={{
              responsive: true,
              plugins: {
                legend: { display: true, position: "bottom" },
                tooltip: {
                  callbacks: {
                    afterBody: (items) => {
                      const idx = items[0].dataIndex;
                      const rate =
                        testsLeaderboard[idx]?.test_completion_rate || 0;
                      return `Completion Rate: ${rate}%`;
                    },
                  },
                },
              },
              scales: {
                x: { stacked: true },
                y: { beginAtZero: true, stacked: true },
              },
            }}
          />
        </ChartCard>

        {/* Row 5: Time Spent (Full width) */}
        <ChartCard title="Time Spent (seconds)" className="chart-card--full">
          <BarChart
            data={{
              labels: timeSpentLeaderboard.map((c) => c.course_name),
              datasets: [
                {
                  label: "Time Spent (min)",
                  data: timeSpentLeaderboard.map(
                    (c) => toMinutes?.(c.total_time_spent_seconds) || 0,
                  ),
                  backgroundColor: chartPalette.solid.orange,
                  hoverBackgroundColor: chartPalette.hover.orange,
                  borderRadius: 10,
                },
              ],
            }}
            options={{ responsive: true, scales: { y: { beginAtZero: true } } }}
          />
        </ChartCard>
      </div>
      {hoveredLegend && (
        <div
          className="chart-legend-tooltip"
          style={{
            left: hoveredLegend.x + 15,
            top: hoveredLegend.y - 10,
          }}
        >
          {hoveredLegend.text}
        </div>
      )}
    </section>
  );
};

export default OverallDashboardView;
